import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FiMail, FiArrowLeft, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import companyLogo from '../assets/logo.png';
import toast from 'react-hot-toast';
import { baseURL } from '../finance/api/client';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(`${baseURL}/auth/forgot-password`, {
        email: email.trim(),
      });

      if (response.status === 200) {
        setSubmitted(true);
        toast.success('Reset link request processed');
      } else {
        toast.error('Unable to process request. Please try again.');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Unable to process request. Please try again later.';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100/70 font-sans p-3 sm:p-6 lg:p-8">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100 my-auto">
        {/* Branding Section — Top Banner on Mobile, Left Column on Laptop */}
        <div className="w-full lg:w-1/2 bg-slate-900 flex flex-col items-center justify-center py-8 px-6 sm:py-10 sm:px-8 lg:p-12 text-white relative overflow-hidden shrink-0">
          {/* Decorative shapes */}
          <div className="absolute top-[-20%] left-[-10%] w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-blue-600/20 blur-3xl mix-blend-screen pointer-events-none"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-indigo-500/20 blur-3xl mix-blend-screen pointer-events-none"></div>

          <div className="relative z-10 flex flex-col items-center justify-center my-auto">
            <img
              src={companyLogo}
              alt="Orakris Logo"
              className="w-44 sm:w-56 lg:w-64 h-auto object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500 ease-out"
            />
          </div>

          <div className="relative z-10 w-full text-center mt-auto pt-6 hidden lg:block">
            <p className="text-sm text-slate-400/80 font-medium tracking-wide">© 2026 Orakris. All rights reserved.</p>
          </div>
        </div>

        {/* Right Side - Forgot Password Form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center p-6 sm:p-10 lg:p-16 relative bg-white">
          <div className="max-w-md w-full mx-auto">
            <div className="mb-8 sm:mb-10 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1.5 tracking-tight">Forgot Password</h2>
              <p className="text-slate-500 text-sm sm:text-base leading-relaxed">
                Enter the email address associated with your account and we will send you a password reset link.
              </p>
            </div>

            {submitted ? (
              <div className="space-y-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <FiCheckCircle className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Check Your Email</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    If an account exists with <strong className="text-slate-800">{email}</strong>, a password reset link has been sent.
                  </p>
                  <p className="text-xs text-slate-500 mt-3">
                    The reset link will expire in <span className="font-semibold text-slate-700">30 minutes</span>. Please make sure to check your spam/junk folder.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                    }}
                    className="w-full py-2.5 px-4 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Resend link or try another email
                  </button>

                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <FiArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700 text-sm">
                    <FiAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Registered Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FiMail className="text-slate-400" />
                    </div>
                    <input
                      id="reset-email"
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm bg-slate-50 focus:bg-white"
                      placeholder="name@example.com"
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending Reset Link...</span>
                    </div>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
                  >
                    <FiArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </form>
            )}

            <div className="mt-8 text-center border-t border-slate-100 pt-6">
              <p className="text-xs text-slate-400">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
